import { fetchSupabaseFunction } from "@/lib/shopifySessionToken";
import { getShopifyEmbeddedIdToken } from "@/lib/shopifyEmbedded";
import type { PendingMerchantSetup } from "@/lib/merchantSetup";

export async function registerMerchantAccount(params: {
  email: string;
  password: string;
  merchantSetup: PendingMerchantSetup;
}) {
  const response = await fetchSupabaseFunction("register-merchant-account", {
    body: {
      accountType: "business",
      email: params.email,
      merchantSetup: params.merchantSetup,
      password: params.password,
    },
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || "Failed to register merchant account");
  }

  return payload;
}

export async function registerShopifyMerchantAccount(sessionToken: string, shopifySessionToken?: string | null) {
  const response = await fetchSupabaseFunction("register-shopify-merchant", {
    body: { sessionToken },
    headers: getShopifyLaunchHeaders(shopifySessionToken),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error || "Failed to create Shopify merchant account");
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  return payload as {
    success: boolean;
    email: string;
    userId: string;
    tokenHash: string;
  };
}

export async function prepareShopifyEmbeddedInstall(shopifySessionToken?: string | null) {
  const response = await fetchSupabaseFunction("prepare-shopify-embedded-install", {
    body: {},
    headers: getShopifyLaunchHeaders(shopifySessionToken),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error || "Failed to connect the Shopify store");
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  return payload as {
    success?: boolean;
    requiresLogin?: boolean;
    sessionToken?: string;
    email: string;
    shop: string;
    shopName: string;
    shopCurrency: string;
  };
}

function getShopifyLaunchHeaders(shopifySessionToken?: string | null): Record<string, string> {
  if (typeof window === "undefined") {
    return {};
  }

  const urlToken = new URLSearchParams(window.location.search).get("id_token")?.trim();
  const sessionToken = shopifySessionToken?.trim() || urlToken || getShopifyEmbeddedIdToken();
  return sessionToken
    ? {
        "X-Shopify-Session-Token": sessionToken,
        "X-Shopify-Embedded": "1",
      }
    : {};
}

export async function verifyMerchantEmail(token: string) {
  const response = await fetchSupabaseFunction("verify-merchant-email", {
    body: { token },
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || "Failed to verify email");
  }

  return payload;
}

export async function resendMerchantVerificationEmail(email: string) {
  const response = await fetchSupabaseFunction("resend-merchant-verification", {
    body: { email },
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || "Failed to resend verification email");
  }

  return payload;
}
